import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface ErrorReportData {
  url: string;
  fullUrl: string;
  userAgent: string;
  timestamp: string;
  userId?: number;
  userEmail?: string;
  userName?: string;
  description: string;
  screenshot?: string;
  pageTitle: string;
  errorType: 'bug' | 'feature' | 'ui' | 'performance' | 'other';
  severity: 'low' | 'medium' | 'high' | 'critical';
  reproductionSteps?: string;
  expectedBehavior?: string;
  actualBehavior?: string;
  browserInfo: {
    name: string;
    version: string;
    platform: string;
    language: string;
    viewport: string;
    colorDepth: number;
    timezone: string;
  };
  pageContext: {
    referrer: string;
    params: Record<string, string>;
    searchParams: Record<string, string>;
    hash: string;
  };
}

export const useErrorReport = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Función para capturar toda la información del contexto automáticamente
  const captureContext = useCallback((): Omit<ErrorReportData, 'description' | 'errorType' | 'severity' | 'screenshot' | 'reproductionSteps' | 'expectedBehavior' | 'actualBehavior'> => {
    // Detectar navegador
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown';
      let browserVersion = 'Unknown';

      if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
        browserName = 'Chrome';
        const match = userAgent.match(/Chrome\/([0-9.]+)/);
        browserVersion = match ? match[1] : 'Unknown';
      } else if (userAgent.includes('Firefox')) {
        browserName = 'Firefox';
        const match = userAgent.match(/Firefox\/([0-9.]+)/);
        browserVersion = match ? match[1] : 'Unknown';
      } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
        browserName = 'Safari';
        const match = userAgent.match(/Version\/([0-9.]+)/);
        browserVersion = match ? match[1] : 'Unknown';
      } else if (userAgent.includes('Edg')) {
        browserName = 'Edge';
        const match = userAgent.match(/Edg\/([0-9.]+)/);
        browserVersion = match ? match[1] : 'Unknown';
      }

      return { name: browserName, version: browserVersion };
    };

    // Extraer parámetros de la URL
    const urlParams = new URLSearchParams(location.search);
    const searchParams: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      searchParams[key] = value;
    });

    // Extraer parámetros de la ruta
    const params: Record<string, string> = {};
    // Si estamos en /servicios/123, extraer el ID
    const pathSegments = location.pathname.split('/').filter(Boolean);
    pathSegments.forEach((segment, index) => {
      if (index > 0 && !isNaN(Number(segment))) {
        params[pathSegments[index - 1] + '_id'] = segment;
      }
    });

    const browser = detectBrowser();
    const fullUrl = window.location.href;

    return {
      url: location.pathname,
      fullUrl,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      userId: user?.id,
      userEmail: user?.email,
      userName: user?.nombre ? `${user.nombre} ${user.apellidos || ''}`.trim() : undefined,
      pageTitle: document.title,
      browserInfo: {
        name: browser.name,
        version: browser.version,
        platform: navigator.platform,
        language: navigator.language,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        colorDepth: screen.colorDepth,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      pageContext: {
        referrer: document.referrer,
        params,
        searchParams,
        hash: location.hash
      }
    };
  }, [location, user]);

  // Función para capturar screenshot de la página
  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    try {
      // Usar html2canvas si está disponible
      if (typeof window !== 'undefined' && (window as any).html2canvas) {
        const canvas = await (window as any).html2canvas(document.body, {
          height: window.innerHeight,
          width: window.innerWidth,
          scrollX: 0,
          scrollY: 0,
          useCORS: true,
          allowTaint: true,
          scale: 0.5, // Reducir calidad para menor tamaño
          backgroundColor: '#ffffff'
        });
        return canvas.toDataURL('image/jpeg', 0.7); // Comprimir JPEG
      }

      // Fallback: usar getDisplayMedia API si está disponible
      if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
        console.warn('html2canvas no disponible, usando getDisplayMedia como fallback');
        // Esto requiere interacción del usuario, así que solo como último recurso
        return null;
      }

      return null;
    } catch (error) {
      console.warn('Error capturando screenshot:', error);
      return null;
    }
  }, []);

  // Función principal para crear reporte
  const createErrorReport = useCallback(async (
    description: string,
    errorType: ErrorReportData['errorType'] = 'bug',
    severity: ErrorReportData['severity'] = 'medium',
    additionalInfo?: {
      reproductionSteps?: string;
      expectedBehavior?: string;
      actualBehavior?: string;
    }
  ): Promise<ErrorReportData> => {
    const context = captureContext();
    const screenshot = await captureScreenshot();

    return {
      ...context,
      description,
      errorType,
      severity,
      screenshot: screenshot || undefined,
      reproductionSteps: additionalInfo?.reproductionSteps,
      expectedBehavior: additionalInfo?.expectedBehavior,
      actualBehavior: additionalInfo?.actualBehavior
    };
  }, [captureContext, captureScreenshot]);

  // Función para enviar reporte al backend
  const sendErrorReport = useCallback(async (reportData: ErrorReportData): Promise<boolean> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/error-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('Error sending report:', error);
      return false;
    }
  }, []);

  // Función simplificada para reportar error rápido
  const reportError = useCallback(async (
    description: string, 
    errorType?: ErrorReportData['errorType'],
    severity?: ErrorReportData['severity']
  ): Promise<boolean> => {
    try {
      const reportData = await createErrorReport(description, errorType, severity);
      return await sendErrorReport(reportData);
    } catch (error) {
      console.error('Error creating error report:', error);
      return false;
    }
  }, [createErrorReport, sendErrorReport]);

  return {
    captureContext,
    captureScreenshot,
    createErrorReport,
    sendErrorReport,
    reportError
  };
};